import React, { useEffect, useState } from "react";
import {
  useFormikContext,
  FormikContextType,
  FieldAttributes,
  useField,
  ErrorMessage,
} from "formik";
import toPath from "lodash/toPath";
import classNames from "classnames";
import { FormField } from "@avamae/formbuilder";

/**
 * Given an object and a path, traverse the object to find the value,
 * and return it.
 */
export function deepGet(obj: any, path: string | string[]) {
  const segments = toPath(path);
  let i = 0;
  let val = obj;
  for (; i < segments.length; i++) {
    if (val === undefined) {
      return val;
    }
    val = val[segments[i]];
  }

  return val;
}

function Input<T>(props: FieldAttributes<T>) {
  const [field, meta] = useField(props);
  const showError = meta.touched && meta.error;
  const { className: existingClasses, ...rest } = props;
  const className = classNames(existingClasses, { ErrorInput: showError });

  return <input {...(field as any)} {...rest} className={className} />;
}

const ErrorM: React.FC<{ name: string }> = props => (
  <ErrorMessage name={props.name}>
    {msg => <div className="ErrorLabel">{msg}</div>}
  </ErrorMessage>
);

export interface BooleanCheckboxProps {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  editReadOnly?: boolean;
}

const BooleanCheckbox = function<T>({
  fieldName,
  label,
  readOnly,
  editReadOnly,
}: BooleanCheckboxProps) {
  const formikContext = useFormikContext<FormikContextType<T>>();
  const [currentValue, setCurrentValue] = useState<boolean | null>(null);

  useEffect(() => {
    const tempCurrentValue = deepGet(formikContext.values, fieldName);
    setCurrentValue(tempCurrentValue);
  }, [formikContext.values]);

  if (readOnly) {
    return (
      <div className="FormBox FormSideBySide">
        <div className="FormLabel">
          <label>{label}</label>
        </div>
        <div className="FormField">
          <Input
            name={fieldName}
            className={"EditBox ViewOnly"}
            readOnly={readOnly}
            value={currentValue ? "Yes" : "No"}
          />
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={`FormBox FormSideBySide Boolean BooleanCheckbox ${
          editReadOnly ? "BooleanCheckboxEditReadOnly EditReadOnly" : ""
        }`}
      >
        {/* <div className="FormLabel NoPaddingTop"> */}
        {/* <div className="FormLabel">
          <label>{label}</label>
        </div> */}

        <div className="FormField">
          <div className="RadioButtons">
            <div
              className={`Radio ${
                currentValue === (true || "Yes") ? "Checked" : ""
              }`}
              role="checkbox"
              aria-checked={
                typeof currentValue === "boolean" ? currentValue : undefined
              }
              onClick={e => {
                e.preventDefault();

                if (!editReadOnly) {
                  console.log("currentValue: ", currentValue);
                  const newValue = !currentValue;
                  console.log("newValue: ", newValue);

                  formikContext.setFieldValue(fieldName as any, newValue);
                }
              }}
            >
              <input
                id={fieldName + "-yes"}
                type="radio"
                name={fieldName}
                value={"true"}
                checked={
                  typeof currentValue === "boolean" ? currentValue : undefined
                }
                onChange={() => {}} // Prevent console error about providing a checked attribute but no onChange prop. The onChange is the onClick on the div that wraps this.
              />
              <label className="CustomInput" htmlFor={fieldName + "-yes"}>
                {label}
              </label>
            </div>
          </div>
        </div>
        <ErrorM name={fieldName.toString()} />
      </div>
    );
  }
};

export { BooleanCheckbox };

type BooleanLabelOverride = {
  [fieldName: string]: {
    true: string;
    false: string;
    null?: string;
  };
};

export const booleanCheckboxRenderer = (
  readOnly: boolean,
  editReadOnly?: boolean
) =>
  function booleanCheckboxRenderer<T>(
    _key: React.ReactText,
    field: FormField<T>
  ) {
    return (
      <BooleanCheckbox
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
        editReadOnly={editReadOnly}
      />
    );
  };
